import Page from '../../../shared/components/Page/Page';
import React, { useState, useEffect } from 'react';
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Space,
    Table,
    message,
    Popconfirm,
    Select,
    Layout,
    Switch,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { getProxies } from '../api';
import { createProxy } from '../api';
import { updateProxy } from '../api';
import { deleteProxy } from '../api';
import { checkProxy } from '../api';
import { checkAllProxies } from '../api';
import { getBanks } from '../api';

const { Content } = Layout;

const Proxy = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingRecord, setEditingRecord] = useState(null);
    const [banks, setBanks] = useState([]);
    const [searchForm] = Form.useForm();
    const [searchParams, setSearchParams] = useState({});
    const [checkingId, setCheckingId] = useState(null);
    const [checkingAll, setCheckingAll] = useState(false);

    const fetchBanks = async () => {
        try {
            const response = await getBanks();
            if (response.success) {
                setBanks(response.data || []);
            }
        } catch (error) {
            message.error('Không thể tải danh sách ngân hàng');
        }
    };

    const fetchData = async (page = 1, pageSize = 10, params = searchParams) => {
        try {
            setLoading(true);
            const payload = {
                pagination: {
                    current: page,
                    pageSize: pageSize
                },
                ...params
            };
            const response = await getProxies( payload);
            if (response.success) {
                setData(response.data.data || []);
                setPagination({
                    current: response.data.currentPage || page,
                    pageSize: response.data.perPage || pageSize,
                    total: response.data.total || 0
                });
            }
        } catch (error) {
            message.error('Không thể tải danh sách proxy');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanks();
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAdd = () => {
        form.resetFields();
        form.setFieldsValue({ isActive: true });
        setEditingRecord(null);
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        form.setFieldsValue({
            id: record.id,
            proxyAddress: record.proxyAddress,
            type: record.type,
            isActive: record.isActive
        });
        setEditingRecord(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await deleteProxy( id);
            if (response.success) {
                message.success('Xóa thành công');
                fetchData(pagination.current, pagination.pageSize);
            } else {
                message.error(response.message || 'Không thể xóa proxy');
            }
        } catch (error) {
            message.error('Không thể xóa proxy');
        }
    };

    const handleSubmit = async (values) => {
        try {
            const payload = {
                id: values.id,
                proxyAddress: values.proxyAddress,
                type: values.type || null,
                isActive: values.isActive
            };

            let response;
            if (editingRecord) {
                response = await updateProxy( payload);
            } else {
                response = await createProxy( payload);
            }

            if (response.success) {
                message.success(editingRecord ? 'Cập nhật thành công' : 'Thêm mới thành công');
                setModalVisible(false);
                fetchData(pagination.current, pagination.pageSize);
            } else {
                message.error(response.message || 'Có lỗi xảy ra khi lưu dữ liệu');
            }
        } catch (error) {
            message.error('Có lỗi xảy ra khi lưu dữ liệu');
        }
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const params = {
            ...searchParams
        };

        // Add filters
        if (filters.type && filters.type.length > 0) {
            params.filters = {
                ...params.filters,
                type: filters.type
            };
        }
        if (filters.isActive && filters.isActive.length > 0) {
            params.filters = {
                ...params.filters,
                isActive: filters.isActive
            };
        }
        if (filters.isOnline && filters.isOnline.length > 0) {
            params.filters = {
                ...params.filters,
                isOnline: filters.isOnline
            };
        }

        // Add sorter
        if (sorter.field) {
            params.sorter = {
                field: sorter.field,
                order: sorter.order === 'ascend' ? 'ascend' : 'descend'
            };
        }

        fetchData(newPagination.current, newPagination.pageSize, params);
        setPagination(newPagination);
    };

    const handleSearch = (values) => {
        const params = {};
        
        if (values.proxyAddress) {
            params.search = { proxyAddress: values.proxyAddress };
        }
        if (values.type) {
            params.filters = { ...params.filters, type: [values.type] };
        }
        if (values.isActive !== undefined && values.isActive !== null) {
            params.filters = { ...params.filters, isActive: [values.isActive] };
        }
        if (values.isOnline !== undefined && values.isOnline !== null) {
            params.filters = { ...params.filters, isOnline: [values.isOnline] };
        }

        setSearchParams(params);
        fetchData(1, pagination.pageSize, params);
    };

    const handleReset = () => {
        searchForm.resetFields();
        const emptyParams = {};
        setSearchParams(emptyParams);
        fetchData(1, pagination.pageSize, emptyParams);
    };

    const handleCheck = async (id) => {
        try {
            setCheckingId(id);
            const response = await checkProxy( id);
            if (response.success) {
                const reassigned = response.data?.reassigned || 0;
                const skipped = response.data?.skipped || 0;
                if (response.data?.isOnline) {
                    message.success('Proxy đang sống');
                } else if (reassigned > 0) {
                    message.warning(`Proxy đã chết. Đã gán lại ${reassigned} tài khoản ngân hàng`);
                } else if (skipped > 0) {
                    message.warning('Proxy đã chết. Không còn proxy sống khác để gán lại');
                } else {
                    message.warning('Proxy đã chết');
                }
                fetchData(pagination.current, pagination.pageSize);
            } else {
                message.error(response.message || 'Không thể kiểm tra proxy');
            }
        } catch (error) {
            message.error('Không thể kiểm tra proxy');
        } finally {
            setCheckingId(null);
        }
    };

    const handleCheckAll = async () => {
        try {
            setCheckingAll(true);
            const response = await checkAllProxies();
            if (response.success) {
                if (response.data?.queued) {
                    message.info(`Đã đưa ${response.data.total} proxy vào hàng đợi kiểm tra`);
                } else {
                    message.success(`Sống: ${response.data?.online || 0}, chết: ${response.data?.offline || 0}, đã gán lại: ${response.data?.reassigned || 0}`);
                    fetchData(pagination.current, pagination.pageSize);
                }
            } else {
                message.error(response.message || 'Không thể kiểm tra proxy');
            }
        } catch (error) {
            message.error('Không thể kiểm tra proxy');
        } finally {
            setCheckingAll(false);
        }
    };

    const renderConnection = (isOnline) => {
        if (isOnline === true) {
            return { label: 'Sống', color: '#52c41a', background: '#f6ffed', border: '#b7eb8f' };
        }
        if (isOnline === false) {
            return { label: 'Chết', color: '#ff4d4f', background: '#fff1f0', border: '#ffa39e' };
        }
        return { label: 'Chưa kiểm tra', color: '#8c8c8c', background: '#fafafa', border: '#d9d9d9' };
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: '5%',
            align: 'center',
        },
        {
            title: 'Địa chỉ Proxy',
            dataIndex: 'proxyAddress',
            key: 'proxyAddress',
            width: '25%',
            render: (text) => <span style={{ fontFamily: 'monospace' }}>{text}</span>
        },
        {
            title: 'Loại',
            dataIndex: 'type',
            key: 'type',
            width: '20%',
            filterMultiple: false,
            filters: banks.map(bank => ({
                text: bank.bankName || bank.bank_name,
                value: bank.bankName || bank.bank_name
            })),
            render: (text) => text || <span style={{ color: '#999' }}>Mặc định</span>
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            width: '10%',
            align: 'center',
            filterMultiple: false,
            filters: [
                { text: 'Hoạt động', value: true },
                { text: 'Không hoạt động', value: false }
            ],
            render: (isActive) => (
                <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: isActive ? '#f6ffed' : '#fff1f0',
                    color: isActive ? '#52c41a' : '#ff4d4f',
                    border: `1px solid ${isActive ? '#b7eb8f' : '#ffa39e'}`
                }}>
                    {isActive ? 'Hoạt động' : 'Không hoạt động'}
                </span>
            ),
        },
        {
            title: 'Kết nối',
            dataIndex: 'isOnline',
            key: 'isOnline',
            width: '14%',
            align: 'center',
            filterMultiple: false,
            filters: [
                { text: 'Sống', value: 'true' },
                { text: 'Chết', value: 'false' },
                { text: 'Chưa kiểm tra', value: 'unchecked' }
            ],
            render: (isOnline, record) => {
                const status = renderConnection(isOnline);
                return (
                    <div>
                        <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: status.background,
                            color: status.color,
                            border: `1px solid ${status.border}`
                        }}>
                            {status.label}
                        </span>
                        <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>
                            {record.lastCheckedAt ? new Date(record.lastCheckedAt).toLocaleString('vi-VN') : '-'}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: '15%',
            render: (text) => text ? new Date(text).toLocaleString('vi-VN') : '-'
        },
        {
            title: 'Ngày cập nhật',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: '15%',
            render: (text) => text ? new Date(text).toLocaleString('vi-VN') : '-'
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: '10%',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        size="small"
                        icon={<SyncOutlined />}
                        loading={checkingId === record.id}
                        onClick={() => handleCheck(record.id)}
                    />
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa proxy này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Đồng ý"
                        cancelText="Hủy"
                    >
                        <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Page title="Quản lý Proxy">
            <Content style={{ padding: '24px' }}>
                <Card title="Quản lý Proxy">
                    <Form
                        form={searchForm}
                        onFinish={handleSearch}
                        style={{ marginBottom: 24 }}
                        layout="inline"
                    >
                        <Row gutter={16} style={{ width: '100%' }}>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="proxyAddress" label="Địa chỉ Proxy">
                                    <Input placeholder="Nhập địa chỉ proxy" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="type" label="Loại">
                                    <Select
                                        allowClear
                                        placeholder="Chọn ngân hàng"
                                        showSearch
                                        optionFilterProp="children"
                                        style={{ width: '100%' }}
                                    >
                                        <Select.Option key="null" value={null}>Mặc định</Select.Option>
                                        {banks.map(bank => (
                                            <Select.Option key={bank.bankName || bank.bank_name} value={bank.bankName || bank.bank_name}>
                                                {bank.bankName || bank.bank_name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="isActive" label="Trạng thái">
                                    <Select allowClear placeholder="Chọn trạng thái" style={{ width: '100%' }}>
                                        <Select.Option value={true}>Hoạt động</Select.Option>
                                        <Select.Option value={false}>Không hoạt động</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="isOnline" label="Kết nối">
                                    <Select allowClear placeholder="Chọn kết nối" style={{ width: '100%' }}>
                                        <Select.Option value={true}>Sống</Select.Option>
                                        <Select.Option value={false}>Chết</Select.Option>
                                        <Select.Option value="unchecked">Chưa kiểm tra</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item>
                                    <Space>
                                        <Button onClick={handleReset}>
                                            Đặt lại
                                        </Button>
                                        <Button type="primary" htmlType="submit">
                                            Tìm kiếm
                                        </Button>
                                        <Button
                                            icon={<SyncOutlined />}
                                            loading={checkingAll}
                                            onClick={handleCheckAll}
                                        >
                                            Kiểm tra tất cả
                                        </Button>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={handleAdd}
                                        >
                                            Thêm mới
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>

                    <Table
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} items`
                        }}
                        onChange={handleTableChange}
                        bordered
                        size="middle"
                        scroll={{ x: 1400 }}
                    />
                </Card>

                <Modal
                    title={editingRecord ? 'Chỉnh sửa Proxy' : 'Thêm Proxy mới'}
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={null}
                    width={500}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        {editingRecord && (
                            <Form.Item
                                name="id"
                                hidden
                            >
                                <Input />
                            </Form.Item>
                        )}
                        <Form.Item
                            name="proxyAddress"
                            label="Địa chỉ Proxy"
                            rules={[
                                { required: true, message: 'Vui lòng nhập địa chỉ proxy' },
                                { pattern: /^[\w.\-:@]+$/, message: 'Định dạng không hợp lệ (VD: 192.168.1.1:8080 hoặc user:pass@host:port)' }
                            ]}
                        >
                            <Input placeholder="192.168.1.1:8080" />
                        </Form.Item>
                        <Form.Item
                            name="type"
                            label="Loại (Tên ngân hàng)"
                            extra="Để trống cho proxy mặc định"
                        >
                            <Select
                                allowClear
                                placeholder="Chọn ngân hàng hoặc để trống"
                                showSearch
                                optionFilterProp="children"
                            >
                                <Select.Option key="null" value={null}>Mặc định (Tất cả ngân hàng)</Select.Option>
                                {banks.map(bank => (
                                    <Select.Option key={bank.bankName || bank.bank_name} value={bank.bankName || bank.bank_name}>
                                        {bank.bankName || bank.bank_name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="isActive"
                            label="Trạng thái"
                            valuePropName="checked"
                        >
                            <Switch checkedChildren="Hoạt động" unCheckedChildren="Không hoạt động" />
                        </Form.Item>
                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit">
                                    {editingRecord ? 'Cập nhật' : 'Thêm mới'}
                                </Button>
                                <Button onClick={() => setModalVisible(false)}>
                                    Hủy
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </Content>
        </Page>
    );
};

export default Proxy;

